import React from 'react';

import {Person} from '@gravity-ui/icons';
import type {SelectOption} from '@gravity-ui/uikit';
import {Flex, Select, User} from '@gravity-ui/uikit';
import block from 'bem-cn-lite';
import {I18n} from 'i18n';
import debounce from 'lodash/debounce';

import type {SubjectClaims} from '../../../../shared/schema/extensions/types';
import {PlaceholderIllustration} from '../../../components/PlaceholderIllustration/PlaceholderIllustration';
import type {AclSubjectSuggestProps} from '../../../registry/units/common/types/components/AclSubjectSuggest';

import './AclSubjectSuggest.scss';

const b = block('cc-acl-subject-suggest');
const i18n = I18n.keyset('component.iam-access-dialog');

const OPTION_HEIGHT = 48;
const getOptionHeight = () => OPTION_HEIGHT;
const filterOption = () => true; // Server-side filtering

export const AclSubjectSuggest: React.FC<AclSubjectSuggestProps> = ({
    fetchSubjects,
    onSubjectChange,
    selectedTab = 'USER_ACCOUNT' as any,
}) => {
    const [subjects, setSubjects] = React.useState<SubjectClaims[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [filter, setFilter] = React.useState('');
    const [hasSearched, setHasSearched] = React.useState(false);

    // Debounced fetch to avoid excessive API calls while typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = React.useCallback(
        debounce(async (query: string) => {
            setLoading(true);
            try {
                const result = await fetchSubjects?.(query, selectedTab);
                if (Array.isArray(result)) {
                    setSubjects(result);
                } else if (result && 'subjects' in result) {
                    setSubjects(result.subjects);
                }
            } catch (error) {
                console.error('Failed to fetch subjects:', error);
                setSubjects([]);
            } finally {
                setLoading(false);
                setHasSearched(true);
            }
        }, 300),
        [fetchSubjects, selectedTab],
    );

    const handleFilterChange = React.useCallback(
        (nextFilter: string) => {
            setFilter(nextFilter);
            setLoading(true);
            debouncedFetch(nextFilter);
        },
        [debouncedFetch],
    );

    const handleOpenChange = React.useCallback(
        (nextOpen: boolean) => {
            if (nextOpen) {
                // Fetch subjects when dropdown opens
                setLoading(true);
                debouncedFetch('');
            } else {
                setFilter('');
                setSubjects([]);
                setLoading(false);
                setHasSearched(false);
            }
        },
        [debouncedFetch],
    );

    const handleUpdate = React.useCallback(
        ([value]: string[]) => {
            if (value) {
                const selectedSubject = subjects.find((subject) => subject.sub === value);
                if (selectedSubject) {
                    onSubjectChange(selectedSubject);
                }
            }
        },
        [subjects, onSubjectChange],
    );

    const options: SelectOption<SubjectClaims>[] = subjects.map((subject) => ({
        value: subject.sub,
        content: subject.name,
        data: subject,
    }));

    return (
        <div className={b('wrapper')}>
            <Select
                value={[]}
                options={options}
                onUpdate={handleUpdate}
                onOpenChange={handleOpenChange}
                placeholder={i18n('action_choose-user')}
                width="max"
                size="l"
                filterable
                filter={filter}
                filterPlaceholder={i18n('action_choose-user')}
                filterOption={filterOption}
                onFilterChange={handleFilterChange}
                loading={loading}
                getOptionHeight={getOptionHeight}
                renderOption={renderSubjectOption}
                renderEmptyOptions={() => (
                    <EmptyStatePlaceholder loading={loading} hasSearched={hasSearched} />
                )}
            />
        </div>
    );
};

function renderSubjectOption(option: SelectOption<SubjectClaims>) {
    const subject = option.data;
    const avatarUrl = subject?.picture || subject?.pictureData;

    return (
        <User
            avatar={
                avatarUrl ? (
                    <img src={avatarUrl} alt={subject?.name || option.value} />
                ) : (
                    {icon: Person}
                )
            }
            name={subject?.name}
            description={subject?.email}
        />
    );
}

function EmptyStatePlaceholder({loading, hasSearched}: {loading: boolean; hasSearched: boolean}) {
    let content: React.ReactNode = null;

    if (!loading && hasSearched) {
        content = (
            <PlaceholderIllustration
                name="notFound"
                size="s"
                direction="column"
                className={b('placeholder')}
                title={i18n('label_not-found')}
            />
        );
    }

    return (
        <Flex alignItems="center" justifyContent="center">
            {content}
        </Flex>
    );
}
